import { useMemo, useState } from 'react'

/**
 * Client-side filter → optional sort → paginate.
 * totalPages is always derived from the filtered set.
 */
export default function useClientTable({
  data = [],
  pageSize = 10,
  filterFn,
  sortFn,
  initialPage = 1,
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(initialPage)

  const filtered = useMemo(() => {
    const source = Array.isArray(data) ? [...data] : []
    const query = searchTerm.trim().toLowerCase()
    let next = source

    if (filterFn) {
      next = next.filter((item) => filterFn(item, query))
    } else if (query) {
      next = next.filter((item) =>
        Object.values(item || {}).some((value) =>
          String(value ?? '')
            .toLowerCase()
            .includes(query)
        )
      )
    }

    if (sortFn) {
      next.sort(sortFn)
    }

    return next
  }, [data, searchTerm, filterFn, sortFn])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize) || 1)
  const safePage = Math.min(currentPage, totalPages)
  const start = (safePage - 1) * pageSize
  const pageItems = filtered.slice(start, start + pageSize)

  const handleSearch = (eventOrValue) => {
    const value =
      typeof eventOrValue === 'string'
        ? eventOrValue
        : eventOrValue?.target?.value ?? ''
    setSearchTerm(value)
    setCurrentPage(1)
  }

  return {
    searchTerm,
    setSearchTerm,
    handleSearch,
    currentPage: safePage,
    setCurrentPage,
    filtered,
    pageItems,
    totalPages,
    totalFiltered: filtered.length,
    pageSize,
    startIndex: start,
  }
}
